/*
 * XYNE Redesign — Quick-view modal
 *
 * Opens on click of any [data-rd-quickview="<handle>"] trigger. Fetches
 * /products/<handle>.js, renders a compact buy-box, and submits ATC through
 * the existing window.RDCart.addItem() so it inherits cart-drawer + toast.
 *
 * State machine: closed → loading → ready. JS keeps a small product-data
 * cache so re-opening the same product doesn't re-fetch.
 */
(function () {
  'use strict';

  function init() {
    var modal = document.getElementById('rd-quickview');
    if (!modal) return;

    var backdrop = document.querySelector('[data-rd-quickview-backdrop]');
    var loading = modal.querySelector('[data-rd-quickview-loading]');
    var content = modal.querySelector('[data-rd-quickview-content]');

    var cache = {};
    var lastFocus = null;
    var currentProduct = null;
    var currentVariantId = null;
    var currentSellingPlanId = null;

    function open() {
      lastFocus = document.activeElement;
      modal.hidden = false;
      if (backdrop) backdrop.hidden = false;
      requestAnimationFrame(function () {
        document.body.classList.add('rd-quickview-open');
      });
      setTimeout(function () {
        var closeBtn = modal.querySelector('[data-rd-quickview-close]');
        if (closeBtn) closeBtn.focus();
      }, 100);
    }

    function close() {
      document.body.classList.remove('rd-quickview-open');
      setTimeout(function () {
        if (!document.body.classList.contains('rd-quickview-open')) {
          modal.hidden = true;
          if (backdrop) backdrop.hidden = true;
          // Reset state
          loading.style.display = '';
          content.hidden = true;
          content.innerHTML = '';
        }
      }, 300);
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
    }

    // Wire close
    modal.querySelectorAll('[data-rd-quickview-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });
    if (backdrop) backdrop.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.body.classList.contains('rd-quickview-open')) close();
    });

    // ---------- Trigger interception ----------
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-rd-quickview]');
      if (!trigger) return;
      var handle = trigger.dataset.rdQuickview;
      if (!handle) return;
      e.preventDefault();
      e.stopPropagation();
      openWithHandle(handle);
    });

    function openWithHandle(handle) {
      open();
      loading.style.display = '';
      content.hidden = true;
      content.innerHTML = '';

      if (cache[handle]) {
        renderProduct(cache[handle]);
        return;
      }

      fetch('/products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin'
      })
        .then(function (r) {
          if (!r.ok) throw new Error('Could not load product.');
          return r.json();
        })
        .then(function (product) {
          cache[handle] = product;
          renderProduct(product);
        })
        .catch(function (err) {
          loading.style.display = 'none';
          content.hidden = false;
          content.innerHTML =
            '<div style="padding:32px;text-align:center;grid-column:1/-1">' +
              '<p style="color:var(--rd-cream);margin:0 0 12px;font-family:var(--rd-font-display);">Couldn\'t load this product.</p>' +
              '<p style="color:var(--rd-mute);font-size:13px;margin:0">' + escapeHtml(err.message || 'Try again or visit the product page.') + '</p>' +
            '</div>';
        });
    }

    // ---------- Render ----------
    function renderProduct(product) {
      currentProduct = product;
      // Pick first available variant; otherwise first variant
      var availableVariant = product.variants.find(function (v) { return v.available; }) || product.variants[0];
      currentVariantId = availableVariant ? availableVariant.id : null;

      // Subscription detection (Shopify Subscriptions native)
      var sellingPlanGroups = product.selling_plan_groups || [];
      var defaultSellingPlan = null;
      if (sellingPlanGroups.length && sellingPlanGroups[0].selling_plans && sellingPlanGroups[0].selling_plans.length) {
        defaultSellingPlan = sellingPlanGroups[0].selling_plans[0];
      }
      currentSellingPlanId = defaultSellingPlan ? defaultSellingPlan.id : null;

      var html = buildHtml(product, availableVariant, defaultSellingPlan);

      loading.style.display = 'none';
      content.hidden = false;
      content.innerHTML = html;

      attachHandlers(product);
    }

    function buildHtml(product, variant, defaultSellingPlan) {
      var images = (product.images && product.images.length) ? product.images : (product.featured_image ? [product.featured_image] : []);
      var firstImg = images[0] || '';

      var onSale = variant && variant.compare_at_price && variant.compare_at_price > variant.price;
      var savingsPct = onSale ? Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) : 0;

      // Description — strip HTML tags and truncate
      var desc = (product.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      if (desc.length > 240) desc = desc.slice(0, 237) + '…';

      // Variant picker — only show if more than the default variant
      var variantsHtml = '';
      var hasMultipleVariants = product.variants && product.variants.length > 1;
      if (hasMultipleVariants && product.options && product.options.length) {
        variantsHtml = product.options.map(function (optName, optIdx) {
          var values = collectOptionValues(product.variants, optIdx);
          var selected = variant ? variant.options[optIdx] : values[0];
          var optionsHtml = values.map(function (val) {
            var matchingVariant = product.variants.find(function (v) {
              if (v.options[optIdx] !== val) return false;
              if (variant) {
                for (var i = 0; i < v.options.length; i++) {
                  if (i !== optIdx && v.options[i] !== variant.options[i]) return false;
                }
              }
              return true;
            });
            var isAvailable = matchingVariant && matchingVariant.available;
            var classes = ['rd-quickview__variant-option'];
            if (val === selected) classes.push('rd-quickview__variant-option--selected');
            if (!isAvailable) classes.push('rd-quickview__variant-option--unavailable');
            return '<button type="button" class="' + classes.join(' ') + '" data-rd-qv-option-idx="' + optIdx + '" data-rd-qv-option-value="' + escapeHtml(val) + '">' + escapeHtml(val) + '</button>';
          }).join('');
          return '<div class="rd-quickview__variants" data-rd-qv-option-group="' + optIdx + '">' +
            '<span class="rd-quickview__variants-label">' + escapeHtml(optName) + ': <strong data-rd-qv-option-label="' + optIdx + '">' + escapeHtml(selected) + '</strong></span>' +
            '<div class="rd-quickview__variant-options">' + optionsHtml + '</div>' +
          '</div>';
        }).join('');
      }

      // Subscription toggle
      var purchaseHtml = '';
      if (defaultSellingPlan) {
        var pct = (defaultSellingPlan.price_adjustments && defaultSellingPlan.price_adjustments[0])
          ? defaultSellingPlan.price_adjustments[0].value
          : 0;
        purchaseHtml =
          '<div class="rd-quickview__purchase">' +
            '<label class="rd-quickview__purchase-option" data-rd-qv-purchase="onetime">' +
              '<input type="radio" name="qv-selling-plan" value="" style="position:absolute;opacity:0;pointer-events:none">' +
              '<span class="rd-quickview__purchase-option-radio"></span>' +
              '<div class="rd-quickview__purchase-option-body">' +
                '<span>One-time purchase</span>' +
                '<span data-rd-qv-onetime-price>' + formatMoney(variant ? variant.price : product.price) + '</span>' +
              '</div>' +
            '</label>' +
            '<label class="rd-quickview__purchase-option rd-quickview__purchase-option--selected" data-rd-qv-purchase="subscribe">' +
              '<input type="radio" name="qv-selling-plan" value="' + defaultSellingPlan.id + '" checked style="position:absolute;opacity:0;pointer-events:none">' +
              '<span class="rd-quickview__purchase-option-radio"></span>' +
              '<div class="rd-quickview__purchase-option-body">' +
                '<span>Subscribe &amp; save' +
                  (pct > 0 ? ' <span class="rd-quickview__purchase-option-savings">Save ' + pct + '%</span>' : '') +
                '</span>' +
              '</div>' +
            '</label>' +
          '</div>';
      }

      var thumbsHtml = '';
      if (images.length > 1) {
        thumbsHtml = '<div class="rd-quickview__thumbs">' +
          images.slice(0, 6).map(function (img, i) {
            var imgUrl = stretchImageUrl(img, 200);
            var bigUrl = stretchImageUrl(img, 800);
            var classes = ['rd-quickview__thumb'];
            if (i === 0) classes.push('rd-quickview__thumb--active');
            return '<button type="button" class="' + classes.join(' ') + '" data-rd-qv-thumb="' + escapeHtml(bigUrl) + '">' +
              '<img src="' + escapeHtml(imgUrl) + '" alt="" loading="lazy" width="56" height="56">' +
            '</button>';
          }).join('') +
        '</div>';
      }

      return '<div class="rd-quickview__media-col">' +
          '<div class="rd-quickview__media">' +
            (onSale ? '<span class="rd-quickview__sale-badge">Save ' + savingsPct + '%</span>' : '') +
            '<img data-rd-qv-main-img src="' + escapeHtml(stretchImageUrl(firstImg, 800)) + '" alt="' + escapeHtml(product.title) + '">' +
          '</div>' +
          thumbsHtml +
        '</div>' +
        '<div class="rd-quickview__info-col">' +
          '<h2 class="rd-quickview__title" id="rd-quickview-title">' + escapeHtml(product.title) + '</h2>' +
          '<div class="rd-quickview__price-row">' +
            '<span class="rd-quickview__price" data-rd-qv-price>' + formatMoney(variant ? variant.price : product.price) + '</span>' +
            (onSale
              ? '<span class="rd-quickview__compare">' + formatMoney(variant.compare_at_price) + '</span><span class="rd-quickview__savings-pill">Save ' + savingsPct + '%</span>'
              : '') +
          '</div>' +
          (desc ? '<p class="rd-quickview__desc">' + escapeHtml(desc) + '</p>' : '') +
          variantsHtml +
          purchaseHtml +
          '<div class="rd-quickview__cta-row">' +
            '<div class="rd-quickview__qty" role="group" aria-label="Quantity">' +
              '<button type="button" data-rd-qv-qty="-1" aria-label="Decrease">−</button>' +
              '<input type="number" data-rd-qv-qty-input value="1" min="1" max="99" aria-label="Quantity">' +
              '<button type="button" data-rd-qv-qty="1" aria-label="Increase">+</button>' +
            '</div>' +
            '<button type="button" class="rd-btn rd-btn--primary rd-quickview__atc" data-rd-qv-atc' +
              (variant && variant.available ? '' : ' disabled') + '>' +
              (variant && variant.available ? 'Add to bag <span aria-hidden="true">→</span>' : 'Sold out') +
            '</button>' +
          '</div>' +
          '<a class="rd-quickview__view-full" href="/products/' + escapeHtml(product.handle) + '">' +
            'View full details ' +
            '<svg viewBox="0 0 24 24" width="14" height="14" fill="none">' +
              '<path d="M5 12h14m-4-4l4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
          '</a>' +
        '</div>';
    }

    function attachHandlers(product) {
      // Variant option clicks
      content.querySelectorAll('[data-rd-qv-option-idx]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.classList.contains('rd-quickview__variant-option--unavailable')) return;
          var optIdx = parseInt(btn.dataset.rdQvOptionIdx, 10);
          var value = btn.dataset.rdQvOptionValue;
          var group = btn.closest('[data-rd-qv-option-group]');
          if (group) {
            group.querySelectorAll('.rd-quickview__variant-option').forEach(function (b) {
              b.classList.remove('rd-quickview__variant-option--selected');
            });
          }
          btn.classList.add('rd-quickview__variant-option--selected');
          var label = content.querySelector('[data-rd-qv-option-label="' + optIdx + '"]');
          if (label) label.textContent = value;
          // Recompute current variant by aggregating selected options
          var selectedValues = [];
          var groups = content.querySelectorAll('[data-rd-qv-option-group]');
          groups.forEach(function (g) {
            var sel = g.querySelector('.rd-quickview__variant-option--selected');
            if (sel) selectedValues.push(sel.dataset.rdQvOptionValue);
          });
          var matched = product.variants.find(function (v) {
            for (var i = 0; i < v.options.length; i++) {
              if (v.options[i] !== selectedValues[i]) return false;
            }
            return true;
          });
          if (matched) {
            currentVariantId = matched.id;
            var priceEl = content.querySelector('[data-rd-qv-price]');
            if (priceEl) priceEl.textContent = formatMoney(matched.price);
            var atc = content.querySelector('[data-rd-qv-atc]');
            if (atc) {
              atc.disabled = !matched.available;
              atc.innerHTML = matched.available
                ? 'Add to bag <span aria-hidden="true">→</span>'
                : 'Sold out';
            }
          }
        });
      });

      // Purchase toggle
      content.querySelectorAll('[data-rd-qv-purchase]').forEach(function (opt) {
        opt.addEventListener('click', function (e) {
          if (e.target.tagName === 'INPUT') return;
          content.querySelectorAll('[data-rd-qv-purchase]').forEach(function (o) {
            o.classList.remove('rd-quickview__purchase-option--selected');
          });
          opt.classList.add('rd-quickview__purchase-option--selected');
          var input = opt.querySelector('input[type="radio"]');
          if (input) input.checked = true;
          currentSellingPlanId = input && input.value ? parseInt(input.value, 10) : null;
        });
      });

      // Quantity
      var qtyInput = content.querySelector('[data-rd-qv-qty-input]');
      content.querySelectorAll('[data-rd-qv-qty]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (!qtyInput) return;
          var change = parseInt(btn.dataset.rdQvQty, 10);
          var current = parseInt(qtyInput.value, 10) || 1;
          qtyInput.value = Math.max(1, Math.min(99, current + change));
        });
      });

      // Thumbnail click — swap main image
      content.querySelectorAll('[data-rd-qv-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.dataset.rdQvThumb;
          if (!src) return;
          content.querySelectorAll('.rd-quickview__thumb').forEach(function (t) {
            t.classList.remove('rd-quickview__thumb--active');
          });
          thumb.classList.add('rd-quickview__thumb--active');
          var mainImg = content.querySelector('[data-rd-qv-main-img]');
          if (mainImg) {
            mainImg.style.opacity = '0';
            setTimeout(function () {
              mainImg.src = src;
              mainImg.style.opacity = '1';
            }, 180);
          }
        });
      });

      // Add to bag — hand off to RDCart
      var atc = content.querySelector('[data-rd-qv-atc]');
      if (atc) {
        atc.addEventListener('click', function () {
          if (atc.disabled) return;
          var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;
          var item = { id: currentVariantId, quantity: qty };
          if (currentSellingPlanId) item.selling_plan = currentSellingPlanId;

          var originalText = atc.innerHTML;
          atc.disabled = true;
          atc.classList.add('rd-btn--loading');
          atc.innerHTML = 'Adding…';

          if (window.RDCart && window.RDCart.addItem) {
            window.RDCart.addItem(item).then(function () {
              // Close the quick-view; cart drawer is already open
              close();
            }).catch(function () {
              // Error toast already fired by RDCart
            }).finally(function () {
              atc.disabled = false;
              atc.classList.remove('rd-btn--loading');
              atc.innerHTML = originalText;
            });
          } else {
            // Fallback — hand off to Atlas Bolt's native <cart-drawer>
            // (or <cart-notification>) so the side drawer slides in instead
            // of doing a full page nav to /cart.
            var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
            var sections = '';
            if (cart && typeof cart.getSectionsToRender === 'function') {
              sections = cart.getSectionsToRender().map(function (s) { return s.id; }).join(',');
            }
            var fd = new FormData();
            fd.append('id', currentVariantId);
            fd.append('quantity', qty);
            if (currentSellingPlanId) fd.append('selling_plan', currentSellingPlanId);
            if (sections) {
              fd.append('sections', sections);
              fd.append('sections_url', window.location.pathname);
            }
            var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
            fetch(addUrl, {
              method: 'POST',
              body: fd,
              credentials: 'same-origin',
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/javascript'
              }
            })
              .then(function (r) { return r.json(); })
              .then(function (response) {
                if (response && response.status) {
                  // Validation error — let user see it on /cart
                  window.location.href = '/cart';
                  return;
                }
                if (cart && typeof cart.renderContents === 'function') {
                  cart.renderContents(response);
                }
                if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
                  window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'quickview' });
                }
                close();
              })
              .catch(function () {
                window.location.href = '/cart';
              })
              .finally(function () {
                atc.disabled = false;
                atc.classList.remove('rd-btn--loading');
                atc.innerHTML = originalText;
              });
          }
        });
      }
    }

    // ---------- Helpers ----------
    function collectOptionValues(variants, optIdx) {
      var seen = [];
      variants.forEach(function (v) {
        var val = v.options[optIdx];
        if (val && seen.indexOf(val) < 0) seen.push(val);
      });
      return seen;
    }

    function stretchImageUrl(url, width) {
      if (!url) return '';
      // Shopify image URLs accept _<W>x in the filename; if absent, try query
      var match = url.match(/^(.*)(\.[a-z]+)(\?.*)?$/i);
      if (match) {
        return match[1] + '_' + width + 'x' + match[2] + (match[3] || '');
      }
      return url;
    }

    function escapeHtml(s) {
      if (s == null) return '';
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function formatMoney(cents) {
      try {
        var shop = (window.Shopify && window.Shopify.currency) || {};
        var f = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: shop.active || 'USD',
          minimumFractionDigits: 2
        });
        return f.format((cents || 0) / 100);
      } catch (e) {
        return '$' + ((cents || 0) / 100).toFixed(2);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
